import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit,
  NgZone,
  ChangeDetectorRef
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validator,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { Country } from '../../../models/locality/country.interface';
import { Address } from '../../../models/locality/address.interface';
import { 
  MapboxAddressService, 
  MapboxAutocompleteResult 
} from '../../../services/mapbox/mapbox-address.service';

const COMPONENT_SELECTOR = 'iad-mapbox-place-input';

/**
 * Input component for address search with Mapbox
 * Inspired by the MapboxSearch component from monovue3
 */
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './mapbox-place-input.component.html',
  styleUrls: ['./mapbox-place-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MatFormFieldControl, useExisting: MapboxPlaceInputComponent },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MapboxPlaceInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MapboxPlaceInputComponent),
      multi: true
    }
  ]
})
export class MapboxPlaceInputComponent
  implements
    OnInit,
    OnDestroy,
    AfterViewInit,
    MatFormFieldControl<Address>,
    ControlValueAccessor,
    Validator {

  // MatFormFieldControl properties
  static nextId = 0;
  ngControl: NgControl = null;
  
  stateChanges: Subject<void>;
  onChange: (value: Address) => void;
  onTouched: () => void;

  @Input() country: Country;
  @Input() displayStreetAndNumber: boolean;
  @Input() placeholder = 'Rechercher une adresse';
  @ViewChild('placeInput', { static: false }) placeInput: ElementRef;

  loading: boolean;
  nameAutoFillDisable: string;
  termSearched: string;
  defaultInputValue: string;
  placeSelected: Address;
  searchControl: FormControl;
  suggestions: MapboxAutocompleteResult[] = [];
  private unsubscribe$ = new Subject<void>();

  id = `${COMPONENT_SELECTOR}-${MapboxPlaceInputComponent.nextId++}`;
  focused = false;
  required = false;
  disabled = false;
  errorState = false;
  controlType = COMPONENT_SELECTOR;
  autofilled?: boolean;
  userAriaDescribedBy?: string;

  get empty(): boolean {
    const value = this.searchControl.value;
    // Handle both string and object values (MapboxAutocompleteResult)
    if (!value) return true;
    if (typeof value === 'string') return value.length === 0;
    // If it's an object (shouldn't happen after our fixes, but safety check)
    return false;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @Input()
  get value(): Address | null {
    return this.placeSelected;
  }
  set value(value: Address | null) {
    this.placeSelected = value;
    this.stateChanges.next();
  }

  get placeholder_(): string {
    return this.placeholder;
  }

  @Input()
  set placeholder_(value: string) {
    this.placeholder = value;
    this.stateChanges.next();
  }

  constructor(
    private mapboxService: MapboxAddressService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.stateChanges = new Subject<void>();
    this.searchControl = new FormControl('');
    this.onChange = () => { /* noop */ };
    this.onTouched = () => { /* noop */ };
  }

  ngOnInit(): void {
    this.nameAutoFillDisable = `mapbox-place-input-${new Date().getTime()}`;
    this.initSearchListener();
  }

  ngAfterViewInit(): void {
    // Initialization after view is ready
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Initialize search change listener
   */
  private initSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => {
          // Store termSearched only if it's a string
          this.termSearched = typeof query === 'string' ? query : '';

          // Always update the address value with the current input
          // But only if query is a string (manual input), not an object (selected suggestion)
          if (query && typeof query === 'string') {
            const manualAddress: Address = {
              address1: query,
              address2: null,
              city: null,
              country: this.country || null,
              postalCode: null,
              latitude: null,
              longitude: null
            };
            this.value = manualAddress;
            this.onChange(this.value);
          }

          if (!query || typeof query !== 'string' || query.length < 3) {
            this.suggestions = [];
            return [];
          }

          this.loading = true;
          const countryCode = (this.country && this.country.isoCode) ? this.country.isoCode : 'FR';
          return this.mapboxService.search(query, countryCode);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (results: MapboxAutocompleteResult[]) => {
          this.ngZone.run(() => {
            this.suggestions = results;
            this.loading = false;
          });
        },
        error => {
          console.error('Error during Mapbox search:', error);
          this.loading = false;
          this.suggestions = [];
        }
      );
  }

  /**
   * Called when user selects a suggestion
   */
  onSuggestionSelected(result: MapboxAutocompleteResult): void {
    if (!result) return;

    this.loading = true;
    this.mapboxService.retrieve(result.value)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        address => {
          this.ngZone.run(() => {
            if (address) {
              this.value = {
                address1: address.address1,
                city: address.city,
                postalCode: address.postalCode,
                latitude: address.latitude ? address.latitude.toString() : undefined,
                longitude: address.longitude ? address.longitude.toString() : undefined,
                state: address.state || undefined,
                country: this.country,
                pathNumber: address.pathNumber || undefined,
                pathName: address.pathName || undefined
              };
              this.defaultInputValue = address.address1;
              // Update searchControl with the string address to avoid keeping the object
              this.searchControl.setValue(address.address1, { emitEvent: false });
              this.onChange(this.value);
              this.onTouched();
            }
            this.loading = false;
          });
        },
        error => {
          this.ngZone.run(() => {
            console.error('Error retrieving details:', error);
            this.loading = false;
          });
        }
      );
  }

  /**
   * Handle manual changes in the input
   */
  onInputChange(value: string): void {
    this.termSearched = value;
    this.searchControl.setValue(value);
    
    // Create an address object with the manually entered text
    const manualAddress: Address = {
      address1: value,
      address2: null,
      city: null,
      country: this.country || null,
      postalCode: null,
      latitude: null,
      longitude: null
    };
    
    this.value = manualAddress;
    this.onChange(this.value);
  }

  // ControlValueAccessor implementation
  writeValue(value: Address | string | null): void {
    if (value) {
      if (typeof value === 'string') {
        this.defaultInputValue = value;
        this.searchControl.setValue(value, { emitEvent: false });
        this.placeSelected = {
          address1: value,
          address2: null,
          city: null,
          country: null,
          postalCode: null,
          latitude: null,
          longitude: null
        } as Address;
      } else {
        this.placeSelected = value;
        this.defaultInputValue = value.address1 || '';
        this.searchControl.setValue(this.defaultInputValue, { emitEvent: false });
      }
    } else {
      this.placeSelected = null;
      this.defaultInputValue = '';
      this.searchControl.setValue('', { emitEvent: false });
    }

    // Force native input update and change detection
    setTimeout(() => {
      if (this.placeInput && this.placeInput.nativeElement) {
        this.placeInput.nativeElement.value = this.searchControl.value || '';
      }
      this.cdr.detectChanges();
    }, 0);
    
    this.stateChanges.next();
  }

  registerOnChange(fn: (value: Address) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
    this.stateChanges.next();
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }

  // MatFormFieldControl implementation
  setDescribedByIds(ids: string[]): void {
    this.userAriaDescribedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      if (this.placeInput && this.placeInput.nativeElement) {
        this.placeInput.nativeElement.focus();
      }
    }
  }

  onFocusIn(event: FocusEvent): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this.focused) {
      return;
    }
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  // Helper methods
  displayFn(result: MapboxAutocompleteResult): string {
    return result ? result.title : '';
  }
}

